/**
 * Role Management API Service
 */

import { apiClient } from '../config';
import type {
  Role,
  RoleWithPermissions,
  Permission,
  PermissionGroup,
  CreateRoleRequest,
  UpdateRoleRequest,
} from '../types';

export const RoleService = {
  // Roles
  async listRoles(params?: {
    page?: number;
    size?: number;
    scope?: string;
    organization_id?: string;
    search?: string;
    include_system?: boolean;
  }): Promise<{ data: Role[]; total: number; page: number; size: number; pages: number }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.size) searchParams.set('size', params.size.toString());
    if (params?.scope) searchParams.set('scope', params.scope);
    if (params?.organization_id) searchParams.set('organization_id', params.organization_id);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.include_system !== undefined) searchParams.set('include_system', params.include_system.toString());

    const query = searchParams.toString();
    const response = await apiClient.get(`/api/roles${query ? `?${query}` : ''}`);
    return response.data;
  },

  async getRole(id: string): Promise<RoleWithPermissions> {
    const response = await apiClient.get(`/api/roles/${id}`);
    return response.data.data;
  },

  async createRole(data: CreateRoleRequest): Promise<Role> {
    const response = await apiClient.post('/api/roles', data);
    return response.data.data;
  },

  async updateRole(id: string, data: UpdateRoleRequest): Promise<Role> {
    const response = await apiClient.put(`/api/roles/${id}`, data);
    return response.data.data;
  },

  async deleteRole(id: string): Promise<void> {
    await apiClient.delete(`/api/roles/${id}`);
  },

  async cloneRole(id: string, data: { name: string; organization_id?: string }): Promise<Role> {
    const response = await apiClient.post(`/api/roles/${id}/clone`, data);
    return response.data.data;
  },

  // Role Permissions
  async updateRolePermissions(
    roleId: string,
    permissions: Array<{ permission_id: string; granted?: boolean; conditions?: any }>
  ): Promise<void> {
    await apiClient.put(`/api/roles/${roleId}/permissions`, { permissions });
  },

  async addRolePermission(
    roleId: string,
    data: { permission_id: string; granted?: boolean; conditions?: any }
  ): Promise<void> {
    await apiClient.post(`/api/roles/${roleId}/permissions`, data);
  },

  async removeRolePermission(roleId: string, permissionId: string): Promise<void> {
    await apiClient.delete(`/api/roles/${roleId}/permissions/${permissionId}`);
  },

  // Permissions
  async listPermissions(params?: {
    category?: string;
    resource?: string;
    search?: string;
  }): Promise<{ data: Permission[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.resource) searchParams.set('resource', params.resource);
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    const response = await apiClient.get(`/api/permissions${query ? `?${query}` : ''}`);
    return response.data;
  },

  async getPermissionsGrouped(): Promise<PermissionGroup[]> {
    const response = await apiClient.get('/api/permissions/grouped');
    return response.data.data;
  },
};
